import { RealtimeChannel } from '@fluentsms/lib-subscriptions';
import React, { useCallback, useContext, useRef } from 'react';
import { AblyContext } from './AblyProvider';

// adapted from https://github.com/mayteio/use-pusher

export interface ChannelsContextType {
  subscribe: <T extends RealtimeChannel>(channelName: string) => T | undefined;
  unsubscribe: (channelName: string) => void;
  getChannel: <T extends RealtimeChannel>(channelName: string) => T | undefined;
  debugEnabled: boolean;
}
export const ChannelsContext = React.createContext<ChannelsContextType>(({} as unknown) as ChannelsContextType);

type AcceptedChannels = RealtimeChannel;
type ConnectedChannels = {
  [channelName: string]: AcceptedChannels[];
};

/**
 * Provider that creates your channels instances and provides it to child hooks throughout your app.
 */

export const ChannelsProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { client, debugEnabled } = useContext(AblyContext);
  const connectedChannels = useRef<ConnectedChannels>({});

  const subscribe = useCallback(
    <T extends RealtimeChannel>(channelName: string) => {
      /** Return early if there's no client */
      if (!client || !channelName) return;

      /** Subscribe to channel and set it in state */
      const ablyChannel = client.channels.get(channelName);
      if (debugEnabled) {
        console.log('Connecting to channel', channelName);
      }
      connectedChannels.current[channelName] = [...(connectedChannels.current[channelName] || []), ablyChannel];
      return ablyChannel as T;
    },
    [client, debugEnabled],
  );

  const unsubscribe = useCallback(
    (channelName: string) => {
      /** Return early if there's no props */
      if (!client || !channelName || !(channelName in connectedChannels.current)) return;
      /** If just one connection, unsubscribe totally */

      if (connectedChannels.current[channelName].length === 1) {
        const channel = client.channels.get(channelName);
        if (debugEnabled) {
          console.log('Disconnecting from channel', channelName);
        }

        channel.unsubscribe();
        delete connectedChannels.current[channelName];
      } else {
        connectedChannels.current[channelName].pop();
      }
    },
    [client, debugEnabled],
  );

  const getChannel = useCallback(
    <T extends RealtimeChannel>(channelName: string) => {
      /** Return early if there's no client */
      if (!client || !channelName || !(channelName in connectedChannels.current)) return;
      /** Return channel */
      return connectedChannels.current[channelName][0] as T;
    },
    [connectedChannels, client],
  );

  return (
    <ChannelsContext.Provider
      value={{
        unsubscribe,
        subscribe: subscribe,
        getChannel: getChannel,
        debugEnabled,
      }}
    >
      {children}
    </ChannelsContext.Provider>
  );
};
